/**
 * Checklist Store - Zustand med Cosmos DB-synk
 * 
 * Hanterar checklistor för alla 9 beredskapsområden.
 * Synkroniserar med backend API och fungerar offline med localStorage.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from './auth-store';
import { logChecklistAction } from '@/lib/activity-logger';

// ============================================================================
// Types
// ============================================================================

export type PrepArea = 'water' | 'food' | 'shelter' | 'health' | 'communication' | 'information' | 'energy' | 'safety' | 'transport' | 'shopping' | 'gardening' | 'other';
export type TimeLevel = '7d' | '30d' | '90d';

export interface ChecklistItem {
  id: string;
  text: string;
  area: PrepArea;
  level: TimeLevel;
  description?: string;
}

export interface ChecklistItemStatus {
  itemId: string;
  isCompleted: boolean;
  completedAt?: string;
  notes?: string;
}

// Antal items per område (baserat på defaultChecklists)
const ITEMS_PER_AREA: Record<PrepArea, number> = {
  health: 19,
  energy: 13,
  food: 12,
  shelter: 10,
  communication: 11,
  transport: 13,
  water: 6,
  information: 9,
  safety: 10,
  shopping: 0,
  gardening: 0,
  other: 0,
};

// Item ID prefix till område mappning
const AREA_PREFIX_MAP: Record<string, PrepArea> = {
  'h': 'health',
  'e': 'energy',
  'f': 'food',
  's': 'shelter',
  'c': 'communication',
  't': 'transport',
  'w': 'water',
  'i': 'information',
  'sa': 'safety',
};

interface ChecklistState {
  // Lokal status för varje item (key = itemId)
  itemStatus: Record<string, ChecklistItemStatus>;
  
  // Senaste synk-tidpunkt
  lastSyncAt: string | null;
  
  // Om vi har osynkade ändringar
  hasPendingChanges: boolean;
  
  // Synk-status
  isSyncing: boolean;
  syncError: string | null;
  
  // Actions
  toggleItem: (itemId: string, area: PrepArea) => void;
  setItemNotes: (itemId: string, notes: string) => void;
  getItemStatus: (itemId: string) => ChecklistItemStatus | null;
  isItemCompleted: (itemId: string) => boolean;
  getCompletedCount: (area: PrepArea, items: ChecklistItem[]) => number;
  getAreaProgress: (area: PrepArea, items: ChecklistItem[]) => number;
  getProgressByArea: (area: PrepArea) => { progress: number; items: number; completed: number };
  
  // Synk
  syncItemToServer: (itemId: string, area: PrepArea, isCompleted: boolean) => Promise<void>;
  syncToServer: () => Promise<void>;
  fetchFromServer: () => Promise<void>;
  
  // Hydration
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// API Client med automatisk token refresh
import { apiGet, apiPut, apiPost } from '@/services/api-client';

// ============================================================================
// Store
// ============================================================================

export const useChecklistStore = create<ChecklistState>()(
  persist(
    (set, get) => ({
      itemStatus: {},
      lastSyncAt: null,
      hasPendingChanges: false,
      isSyncing: false,
      syncError: null,
      _hasHydrated: false,

      toggleItem: (itemId: string, area: PrepArea) => {
        const currentStatus = get().itemStatus[itemId];
        const isCompleted = !currentStatus?.isCompleted;
        
        set((state) => ({
          itemStatus: {
            ...state.itemStatus,
            [itemId]: {
              itemId,
              isCompleted,
              completedAt: isCompleted ? new Date().toISOString() : undefined,
              notes: currentStatus?.notes,
            },
          },
          hasPendingChanges: true,
        }));

        // Log activity
        logChecklistAction('TOGGLE_ITEM', itemId, {
          area,
          isCompleted,
        });

        // Försök synka direkt till servern
        get().syncItemToServer(itemId, area, isCompleted);
      },

      setItemNotes: (itemId: string, notes: string) => {
        set((state) => ({
          itemStatus: {
            ...state.itemStatus,
            [itemId]: {
              ...state.itemStatus[itemId],
              itemId,
              isCompleted: state.itemStatus[itemId]?.isCompleted || false,
              notes,
            },
          },
          hasPendingChanges: true,
        }));
      },

      getItemStatus: (itemId: string) => {
        return get().itemStatus[itemId] || null;
      },

      isItemCompleted: (itemId: string) => {
        return get().itemStatus[itemId]?.isCompleted || false;
      },

      getCompletedCount: (area: PrepArea, items: ChecklistItem[]) => {
        const areaItems = items.filter(item => item.area === area);
        return areaItems.filter(item => get().itemStatus[item.id]?.isCompleted).length;
      },

      getAreaProgress: (area: PrepArea, items: ChecklistItem[]) => {
        const areaItems = items.filter(item => item.area === area);
        if (areaItems.length === 0) return 0;
        const completed = areaItems.filter(item => get().itemStatus[item.id]?.isCompleted).length;
        return Math.round((completed / areaItems.length) * 100);
      },

      // Hämta progress för ett område utan att behöva items-array
      getProgressByArea: (area: PrepArea) => {
        const totalItems = ITEMS_PER_AREA[area] || 0;
        const { itemStatus } = get();
        
        // Räkna hur många items i detta område som är completed
        // Item IDs har prefix som motsvarar området (h=health, e=energy, etc.)
        let completed = 0;
        for (const [itemId, status] of Object.entries(itemStatus)) {
          if (!status.isCompleted) continue;
          
          // Bestäm område baserat på item ID prefix
          let itemArea: PrepArea | null = null;
          if (itemId.startsWith('sa')) {
            itemArea = 'safety';
          } else {
            const prefix = itemId[0];
            itemArea = AREA_PREFIX_MAP[prefix] || null;
          }
          
          if (itemArea === area) {
            completed++;
          }
        }
        
        const progress = totalItems > 0 ? Math.round((completed / totalItems) * 100) : 0;
        return { progress, items: totalItems, completed };
      },

      // Synka enskild item till servern
      syncItemToServer: async (itemId: string, area: PrepArea, isCompleted: boolean) => {
        // Prefer owner's familyId when viewing as a family member
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const familyId = ownerId || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
        const result = await apiPut(`/checklists/${area}/items/${itemId}${qs}`, { isCompleted });
        if (result.success) {
          console.log('Item synced to server:', itemId);
        }
      },

      // Bulk-synk alla ändringar till servern
      syncToServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          set({ syncError: 'Not authenticated' });
          return;
        }

        set({ isSyncing: true, syncError: null });

        try {
          // Gruppera items per område
          const checklists: Record<string, Array<{ itemId: string; isCompleted: boolean; completedAt?: string; notes?: string }>> = {};
          
          for (const [itemId, status] of Object.entries(get().itemStatus)) {
            // Extrahera area från itemId (format: "h1", "e2", etc.)
            const areaPrefix = itemId[0];
            const areaMap: Record<string, PrepArea> = {
              'h': 'health',
              'e': 'energy',
              'f': 'food',
              's': 'shelter',
              'c': 'communication',
              't': 'transport',
              'w': 'water',
              'i': 'information',
              'y': 'safety',
            };
            const area = areaMap[areaPrefix] || 'health';
            
            if (!checklists[area]) {
              checklists[area] = [];
            }
            
            checklists[area].push({
              itemId,
              isCompleted: status.isCompleted,
              completedAt: status.completedAt,
              notes: status.notes,
            });
          }

          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId2 = getEffectiveFamilyOwnerId();
          const familyId2 = ownerId2 || useAuthStore.getState().user?.familyId;
          const qs2 = familyId2 ? `?familyId=${encodeURIComponent(familyId2)}` : '';
          const result = await apiPost(`/checklists/sync${qs2}`, { checklists });

          if (!result.success) {
            throw new Error('Sync failed');
          }

          set({
            isSyncing: false,
            hasPendingChanges: false,
            lastSyncAt: new Date().toISOString(),
          });

          console.log('Checklists synced to server');
        } catch (error) {
          console.error('Failed to sync checklists:', error);
          set({
            isSyncing: false,
            syncError: 'Failed to sync checklists',
          });
        }
      },

      // Hämta checklistor från servern
      fetchFromServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          // Inte inloggad - använd lokal data
          return;
        }

        set({ isSyncing: true, syncError: null });

        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          const result = await apiGet<{ checklists: Record<string, Array<{ itemId: string; isCompleted: boolean; completedAt?: string; notes?: string }>> }>(`/checklists${qs}`);

          if (!result.success) {
            // Misslyckades (inkl session expired) - använd lokal data
            set({ isSyncing: false });
            return;
          }

          if (result.data?.checklists) {
            // Konvertera server-format till lokal status
            const itemStatus: Record<string, ChecklistItemStatus> = {};
            
            for (const [_area, items] of Object.entries(result.data.checklists)) {
              for (const item of items) {
                itemStatus[item.itemId] = {
                  itemId: item.itemId,
                  isCompleted: item.isCompleted,
                  completedAt: item.completedAt,
                  notes: item.notes,
                };
              }
            }

            set({
              itemStatus,
              isSyncing: false,
              lastSyncAt: new Date().toISOString(),
              hasPendingChanges: false,
            });

            console.log('Checklists fetched from server');
          } else {
            set({ isSyncing: false });
          }
        } catch (error) {
          console.error('Failed to fetch checklists:', error);
          set({
            isSyncing: false,
            syncError: 'Failed to fetch checklists',
          });
        }
      },

      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'beredskapshubben-checklists',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        itemStatus: state.itemStatus,
        lastSyncAt: state.lastSyncAt,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// ============================================================================
// Hooks
// ============================================================================

export const useChecklistHydrated = () => useChecklistStore((state) => state._hasHydrated);
